/**
 * components/wizard/FieldCoverageSilhouette.jsx — Phase E v8c.5.
 *
 * Top-down SVG silhouette of the canonical full pitch with every
 * calibrated zone overlaid in its kind-specific colour:
 *   • Areas / Flächen → semi-transparent filled polygon (#FBBF24)
 *   • Lines / Linien  → thick stroke (#22D3EE)
 *   • Points / Punkte → magenta circular anchor (#F43F5E)
 *
 * Coverage score is the convex-hull area of every world-coord
 * divided by the full pitch area, clamped to [0, 1]. Colour band:
 *   < 40% red · 40-70% orange · > 70% green.
 *
 * Used inside `SanityMultiZoneBlock` (the calibration Sanity panel
 * block) to give the operator instant visual feedback on which
 * regions of the pitch are safely solved against vs uncalibrated
 * dead zones.
 */
import React, { useMemo } from "react";
import { zoneWorldPoints } from "@/lib/fieldPresets";
import {
  coverageScore, coverageColorClass,
} from "@/lib/convexHull";

/* Layout: SVG viewBox is the canonical pitch in metres, padded
   slightly so the boundary line + zone strokes don't clip. */
const VB_PAD_M = 2;
const SVG_HEIGHT_PX = 130;  // compact — fits inside the sanity panel

export default function FieldCoverageSilhouette({
  fmt = "Outdoor",
  fieldProfile,
  basePresetId,
  baseSide,
  extraZones = [],
}) {
  const L = Number(fieldProfile?.field_length_m) || 64;
  const W = Number(fieldProfile?.field_width_m)  || 25;

  // ── Resolve every zone (base + extras) to a {kind, points} list ──
  const renderedZones = useMemo(() => {
    const out = [];
    if (basePresetId) {
      const basePts = zoneWorldPoints(fmt, basePresetId, fieldProfile, baseSide);
      // base is always treated as an AREA (matches the wizard's first
      // 4-corner click pass).
      out.push({ kind: "area", points: basePts, id: "base", presetId: basePresetId });
    }
    extraZones.forEach((z, i) => {
      const k = z.kind
        || ((z.image_points?.length === 1) ? "point"
            : (z.image_points?.length === 2) ? "line" : "area");
      const pts = zoneWorldPoints(fmt, z.preset_id, fieldProfile, z.side);
      out.push({ kind: k, points: pts, id: `extra-${i}`, presetId: z.preset_id });
    });
    return out;
  }, [fmt, basePresetId, baseSide, extraZones, fieldProfile]);

  // ── Coverage score across ALL world-points ──
  const coverage = useMemo(() => {
    const pts = renderedZones.flatMap((z) => z.points);
    return coverageScore(pts, L, W);
  }, [renderedZones, L, W]);

  const pct = Math.round(coverage.score * 100);
  const colorCls = coverageColorClass(coverage.score);

  // ── SVG transform helpers: world (m) → SVG (px). World origin is
  // the back-left corner; SVG +y goes DOWN so we flip the y axis.
  const vbW = L + 2 * VB_PAD_M;
  const vbH = W + 2 * VB_PAD_M;
  const toX = (mx) => mx + VB_PAD_M;
  const toY = (my) => (W - my) + VB_PAD_M;  // flip y

  return (
    <div
      data-testid="cal-coverage-silhouette"
      className="space-y-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Coverage-Vorschau · {L}×{W} m
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Abdeckungs-Qualität:
          </span>
          <span
            data-testid="cal-coverage-score"
            className={`font-display font-black text-lg tabular-nums ${colorCls}`}
          >
            {pct}%
          </span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        width="100%"
        height={SVG_HEIGHT_PX}
        preserveAspectRatio="xMidYMid meet"
        className="block rounded-sm bg-slate-950 border border-slate-800"
        data-testid="cal-coverage-svg"
      >
        {/* Canonical pitch outline — solid white stroke */}
        <rect
          x={toX(0)} y={toY(W)}
          width={L} height={W}
          fill="none"
          stroke="rgba(148, 163, 184, 0.6)" strokeWidth={0.15}
        />
        {/* Endzone divider lines (light grey, thin) */}
        <line
          x1={toX(10)} y1={toY(0)} x2={toX(10)} y2={toY(W)}
          stroke="rgba(148, 163, 184, 0.35)" strokeWidth={0.1}
          strokeDasharray="0.4,0.3"
        />
        <line
          x1={toX(L - 10)} y1={toY(0)} x2={toX(L - 10)} y2={toY(W)}
          stroke="rgba(148, 163, 184, 0.35)" strokeWidth={0.1}
          strokeDasharray="0.4,0.3"
        />

        {/* Convex hull overlay (light green) — only when ≥ 3 points */}
        {coverage.hullVertices.length >= 3 && (
          <polygon
            points={coverage.hullVertices.map(([x, y]) =>
              `${toX(x)},${toY(y)}`
            ).join(" ")}
            fill="rgba(16, 185, 129, 0.10)"
            stroke="rgba(16, 185, 129, 0.55)"
            strokeWidth={0.1}
            strokeDasharray="0.5,0.3"
          />
        )}

        {/* Per-zone overlay (kind-coloured) */}
        {renderedZones.map((zone) => {
          if (zone.kind === "area" && zone.points.length === 4) {
            return (
              <polygon
                key={zone.id}
                data-testid={`cal-coverage-area-${zone.id}`}
                points={zone.points.map(([x, y]) =>
                  `${toX(x)},${toY(y)}`
                ).join(" ")}
                fill="rgba(251, 191, 36, 0.28)"   /* #FBBF24 @ 28% */
                stroke="#FBBF24"
                strokeWidth={0.18}
                strokeLinejoin="round"
              />
            );
          }
          if (zone.kind === "line" && zone.points.length === 2) {
            const [a, b] = zone.points;
            return (
              <line
                key={zone.id}
                data-testid={`cal-coverage-line-${zone.id}`}
                x1={toX(a[0])} y1={toY(a[1])}
                x2={toX(b[0])} y2={toY(b[1])}
                stroke="#22D3EE"                  /* cyan-400 */
                strokeWidth={0.35}
                strokeLinecap="round"
              />
            );
          }
          if (zone.kind === "point" && zone.points.length === 1) {
            const [x, y] = zone.points[0];
            return (
              <circle
                key={zone.id}
                data-testid={`cal-coverage-point-${zone.id}`}
                cx={toX(x)} cy={toY(y)}
                r={0.6}
                fill="#F43F5E"                    /* rose-500 */
                stroke="#FFFFFF"
                strokeWidth={0.12}
              />
            );
          }
          return null;
        })}
      </svg>
      <div className="flex items-center justify-between gap-2 text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 inline-block bg-amber-400/70 border border-amber-400 rounded-sm" />
            Flächen
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-[2px] inline-block bg-cyan-400" />
            Linien
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 inline-block bg-rose-500 rounded-full" />
            Punkte
          </span>
        </div>
        <span className="tabular-nums">
          Hull: {coverage.hullAreaM2.toFixed(0)} / {coverage.fieldAreaM2.toFixed(0)} m²
        </span>
      </div>
    </div>
  );
}
